// Firebase Configuration
// Replace these values with your own Firebase project credentials
// Get them from: https://console.firebase.google.com > Project Settings > Your apps

import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, push, onValue, update, remove, get } from 'firebase/database';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "YOUR_API_KEY",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "your-project.firebaseapp.com",
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || "https://your-project-default-rtdb.firebaseio.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "your-project-id",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "your-project.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abc123"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// ============================================
// PASSWORD VERIFICATION
// ============================================

/**
 * Verify facilitator password
 * Returns { valid: true, role: 'admin' | 'client' } or { valid: false }
 */
export const verifyPassword = async (password) => {
  try {
    const passwordsRef = ref(database, 'config/passwords');
    const snapshot = await get(passwordsRef);
    
    if (!snapshot.exists()) {
      console.error('No passwords configured in Firebase');
      return { valid: false };
    }
    
    const passwords = snapshot.val();
    
    // Check admin password
    if (passwords.admin && password === passwords.admin) {
      return { valid: true, role: 'admin' };
    }
    
    // Check client password
    if (passwords.client && password === passwords.client) {
      return { valid: true, role: 'client' };
    }
    
    return { valid: false };
  } catch (error) {
    console.error('Password verification error:', error);
    return { valid: false };
  }
};

// ============================================
// SESSION MANAGEMENT
// ============================================

/**
 * Create a new session with a unique code
 */
export const createSession = async (facilitatorName = 'Facilitator') => {
  const sessionCode = generateSessionCode();
  const sessionRef = ref(database, `sessions/${sessionCode}`);
  
  const sessionData = {
    code: sessionCode,
    facilitator: facilitatorName,
    createdAt: new Date().toISOString(),
    status: 'active', // active, completed, archived
    settings: {
      anonymousAllowed: true,
      showResultsToParticipants: false
    },
    participants: {},
    resultsRevealed: false
  };
  
  await set(sessionRef, sessionData);
  return sessionCode;
};

/**
 * Generate a unique 6-character session code
 */
const generateSessionCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Check if a session exists and is active
 */
export const validateSession = async (sessionCode) => {
  const sessionRef = ref(database, `sessions/${sessionCode}`);
  const snapshot = await get(sessionRef);
  
  if (!snapshot.exists()) {
    return { valid: false, error: 'Session not found' };
  }
  
  const session = snapshot.val();
  if (session.status !== 'active') {
    return { valid: false, error: 'Session is no longer active' };
  }
  
  return { valid: true, session };
};

/**
 * Subscribe to session updates (real-time)
 */
export const subscribeToSession = (sessionCode, callback) => {
  const sessionRef = ref(database, `sessions/${sessionCode}`);
  return onValue(sessionRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.val());
    }
  });
};

/**
 * Subscribe to participants in a session (real-time)
 */
export const subscribeToParticipants = (sessionCode, callback) => {
  const participantsRef = ref(database, `sessions/${sessionCode}/participants`);
  return onValue(participantsRef, (snapshot) => {
    const participants = [];
    if (snapshot.exists()) {
      snapshot.forEach((child) => {
        participants.push({
          id: child.key,
          ...child.val()
        });
      });
    }
    callback(participants);
  });
};

// ============================================
// PARTICIPANT MANAGEMENT
// ============================================

/**
 * Add a participant to a session
 */
export const addParticipant = async (sessionCode, participantData) => {
  const participantsRef = ref(database, `sessions/${sessionCode}/participants`);
  const newParticipantRef = push(participantsRef);
  
  const participant = {
    name: participantData.name,
    isAnonymous: participantData.isAnonymous || false,
    joinedAt: new Date().toISOString(),
    status: 'in_progress', // in_progress, completed
    responses: {},
    completedAt: null
  };
  
  await set(newParticipantRef, participant);
  return newParticipantRef.key;
};

/**
 * Update participant responses
 */
export const updateParticipantResponses = async (sessionCode, participantId, responses) => {
  const participantRef = ref(database, `sessions/${sessionCode}/participants/${participantId}`);
  await update(participantRef, {
    responses,
    status: 'completed',
    completedAt: new Date().toISOString()
  });
};

/**
 * Update a single response (for real-time progress tracking)
 */
export const updateSingleResponse = async (sessionCode, participantId, questionId, response) => {
  const responseRef = ref(database, `sessions/${sessionCode}/participants/${participantId}/responses/${questionId}`);
  await set(responseRef, response);
};

// ============================================
// SESSION CONTROL
// ============================================

/**
 * Reveal results to participants
 */
export const revealResults = async (sessionCode) => {
  const sessionRef = ref(database, `sessions/${sessionCode}`);
  await update(sessionRef, {
    resultsRevealed: true,
    revealedAt: new Date().toISOString()
  });
};

/**
 * Close a session
 */
export const closeSession = async (sessionCode) => {
  const sessionRef = ref(database, `sessions/${sessionCode}`);
  await update(sessionRef, {
    status: 'completed',
    completedAt: new Date().toISOString()
  });
};

/**
 * Archive a session
 */
export const archiveSession = async (sessionCode) => {
  const sessionRef = ref(database, `sessions/${sessionCode}`);
  await update(sessionRef, {
    status: 'archived',
    archivedAt: new Date().toISOString()
  });
};

// ============================================
// DATA RETRIEVAL
// ============================================

/**
 * Get full session data (one-time fetch)
 */
export const getSession = async (sessionCode) => {
  const sessionRef = ref(database, `sessions/${sessionCode}`);
  const snapshot = await get(sessionRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return snapshot.val();
};

/**
 * Get all participants for a session (one-time fetch)
 */
export const getParticipants = async (sessionCode) => {
  const participantsRef = ref(database, `sessions/${sessionCode}/participants`);
  const snapshot = await get(participantsRef);
  
  const participants = [];
  if (snapshot.exists()) {
    snapshot.forEach((child) => {
      participants.push({
        id: child.key,
        ...child.val()
      });
    });
  }
  
  return participants;
};

export { database, ref, onValue };
